/**
 * UI element: Cookie consent alert
 * requires: bootstrap css
 *
 */
class CookieConsentBanner {
    #cookie_name = 'springy_cookie_consent';
    #cookie_notice_accepted = 'ok';
    #container_id = 's-ui-cc-container';
    #close_button_id = 's-ui-cc-close-btn';
    #read_more_elt_id = 's-ui-cc-read-more-link';

    /**
     * @param {Object} config
     * @param {String} config.placement Placement position for alert 'top' or 'bottom'.
     * @param {String} config.aria_label the aria_label message for accessibility.
     * @param {Number} config.cookie_exp_days Number of days after which 'accepted' cookie expires.
     * @param {Function} config.read_more_callback Callback function that gets bound to 's-ui-cc-read-more-link'.
     * @param {String} config.consent_message Text of alert message.
     * @param {String} config.okay Text of button to accept cookies
     */
    constructor({
        placement = 'bottom',
        aria_label = 'User Privacy Alert',
        cookie_exp_days = 180,
        read_more_callback = null,
        consent_message = 'By using our website you are consenting to our use of cookies in accordance with our cookie policy.',
        okay = 'OK',
    }) {
        this.placement = placement;
        if (['bottom', 'top'].indexOf(this.placement) === -1) {
            this.placement = 'bottom';
        }
        this.aria_label = aria_label;
        this.cookie_exp_days = cookie_exp_days;
        this.read_more_callback = read_more_callback;
        this.consent_message = consent_message;
        this.okay = okay;

        this.content = `<div id="${this.#container_id}" class="container">
            <aside class="navbar navbar-default navbar-fixed-${this.placement} fixed-${this.placement}" id="s-ui-cc-navbar" aria-label="${this.aria_label}">
                <div id="s-ui-cc-main" class="container">
                    <div class="navbar-inner navbar-content-center" id="s-ui-cc-msg-container">
                        <div id="s-ui-cc-msg">${this.consent_message}<button id="${this.#close_button_id}" type="button" class="btn btn-sm btn-light">${this.okay}</button></div>
                    </div>
                </div>
            </aside>
        </div>`;
    }

    #setCookie() {
        const exdate = new Date();
        exdate.setDate(exdate.getDate() + this.cookie_exp_days);
        const cookie_value = encodeURI(this.#cookie_notice_accepted) + ((this.cookie_exp_days === null) ? '' : '; expires=' + exdate.toUTCString());
        document.cookie = `${this.#cookie_name}=${cookie_value}; path=/; samesite=lax; secure;`;
    };

    #getCookie() {
        let index_of_equals;
        let c_name;
        let c_value;
        const cookie_arr = document.cookie.split(';');
        for (let i = 0; i < cookie_arr.length; i++) {
            index_of_equals = cookie_arr[i].indexOf('=');
            c_name = cookie_arr[i].substring(0, index_of_equals).replace(/^\s+|\s+$/g, '');

            if (c_name === this.#cookie_name) {
                c_value = cookie_arr[i].substring(index_of_equals + 1);
                return decodeURI(c_value);
            }
        }
        return null;
    };

    isConsentCookieAccepted() {
        const consent = this.#getCookie();
        return (consent === this.#cookie_notice_accepted);
    };

    /**
     *  Display the cookie consent alert if not already accepted.
     */
    setupAlert() {
        if (this.isConsentCookieAccepted()) {
            return;
        }

        // Add the alert html to the body, pre-pended
        document.body.insertAdjacentHTML('afterbegin', this.content);

        // Close button handler.
        document.getElementById(this.#close_button_id)?.addEventListener('click', () => {
            this.#setCookie();
            const container = document.getElementById(this.#container_id);
            if (container) {
                container.style.display = 'none';
            }
        });

        // Read more handler.
        // assign a href in order for the tabbing to work on this
        // do it here in common because we need to make sure the href is #. (and not generate other tickets)
        const readMore = document.getElementById(this.#read_more_elt_id);
        if (!readMore) {
            return;
        }
        readMore.setAttribute('href', '#');
        readMore.addEventListener('click', (ev) => {
            if (this.read_more_callback) {
                this.read_more_callback.apply(this, [ev]);
            }
        });
    };
}
